import { useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle, Clock, Download, Eye, Phone, RefreshCw, Search, XCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { AgentCreationLogsService } from "@/services/agent/AgentCreationLogsService";
import { AgentCreationLogDetailsSidebar } from "./AgentCreationLogDetailsSidebar";
import { useAgentCreationLogs, useAgentCreationLogStats } from "./hooks/useAgentCreationLogs";
import { RefreshCountdown } from "../ConversationLogs/RefreshCountdown";

import type { AgentCreationLog, AgentCreationLogFilters } from "@/types/agent-creation-logs";
const AgentCreationLogsPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<AgentCreationLogFilters>({});
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<AgentCreationLog | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const { data: logsData, isLoading } = useAgentCreationLogs(filters, page, 50, !isDetailOpen);
  const { data: stats } = useAgentCreationLogStats(filters, !isDetailOpen);

  const handleExport = async () => {
    try {
      const csvContent = await AgentCreationLogsService.exportToCSV(filters);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agent-creation-logs-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      toast.success("Genie creation logs exported");
    } catch (error: any) {
      toast.error(`Export failed: ${error.message}`);
    }
  };

  const handleViewDetails = (log: AgentCreationLog) => {
    setSelectedLog(log);
    setIsDetailOpen(true);
  };

  const handleViewConversations = (log: AgentCreationLog) => {
    if (!log.setup_conversation_id) {
      toast.error("No setup conversation available for this creation log");
      return;
    }
    navigate(`/admin/conversation-logs?tab=monitoring&conversationId=${log.setup_conversation_id}`);
  };

  const getStatusBadge = (status: string | null) => {
    const statusMap: Record<string, { icon: any; className: string; label: string }> = {
      pending: { icon: Clock, className: "bg-genie-background-muted text-genie-text-muted border-genie-border", label: "Pending" },
      in_progress: {
        icon: RefreshCw,
        className: "bg-genie-primary-light text-genie-primary border-genie-primary/20",
        label: "In Progress",
      },
      completed: { icon: CheckCircle, className: "bg-genie-primary-light text-genie-primary border-genie-primary/20", label: "Completed" },
      failed: { icon: XCircle, className: "bg-genie-error-light text-genie-error border-genie-error/20", label: "Failed" },
      partial_success: { icon: AlertCircle, className: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Partial Success" },
    };

    const displayStatus = status || "pending";
    const config = statusMap[displayStatus] || statusMap.pending;
    const Icon = config.icon;

    return (
      <Badge className={cn(config.className, "inline-flex items-center gap-1 border w-fit")}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Zap className="h-8 w-8 text-genie-secondary" />
            <div>
              <h1 className="text-3xl font-bold text-genie-text-primary">Genie Creation Logs</h1>
              <p className="text-genie-text-secondary">Debug and restart genie creation attempts</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <RefreshCountdown isVisible={!isDetailOpen} />
            <Button onClick={handleExport} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Total Creations</p>
                  <p className="text-2xl font-bold text-genie-text-primary">{stats.total}</p>
                </div>
                <Zap className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Completed</p>
                  <p className="text-2xl font-bold text-genie-primary">{stats.completed}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-genie-primary" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">Failed</p>
                  <p className="text-2xl font-bold text-genie-error">{stats.failed}</p>
                </div>
                <XCircle className="h-8 w-8 text-genie-error" />
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-genie-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-genie-text-secondary">In Progress</p>
                  <p className="text-2xl font-bold text-genie-accent-dark">{stats.in_progress}</p>
                </div>
                <Clock className="h-8 w-8 text-genie-accent-dark" />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Search genie name..."
                className="pl-10"
                value={filters.search || ""}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? undefined : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="partial_success">Partial Success</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.creation_source || "all"}
              onValueChange={(value) => setFilters({ ...filters, creation_source: value === "all" ? undefined : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="setup_conversation">Setup Conversation</SelectItem>
                <SelectItem value="direct_create">Direct Create</SelectItem>
                <SelectItem value="manual_retry">Manual Retry</SelectItem>
                <SelectItem value="api">API</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setFilters({})} className="w-full">
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg border border-genie-border">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Genie Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>User Email</TableHead>
                  <TableHead>Retries</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Conv</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-genie-text-secondary">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                      Loading genie creation logs...
                    </TableCell>
                  </TableRow>
                ) : logsData?.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-genie-text-muted">
                      No genie creation logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logsData?.data.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium text-genie-text-primary">{log.agent_name || "Unnamed"}</TableCell>
                      <TableCell>{getStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">{log.creation_source || "—"}</TableCell>
                      <TableCell className="text-sm text-genie-text-secondary max-w-xs"><CopyableEmail email={log.user_email} textClassName="truncate" /></TableCell>
                      <TableCell>
                        {log.retry_count && log.retry_count > 0 ? (
                          <Badge variant="secondary">{log.retry_count}</Badge>
                        ) : (
                          <span className="text-genie-text-muted">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">{new Date(log.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-genie-text-secondary">
                        {log.completed_at ? new Date(log.completed_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewConversations(log)}
                          disabled={!log.setup_conversation_id}
                          title={log.setup_conversation_id ? "View setup conversation for this agent" : "No setup conversation available"}
                        >
                          <Phone className={`h-4 w-4 ${!log.setup_conversation_id ? "opacity-50" : ""}`} />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetails(log)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {isLoading ? (
              <div className="text-center py-8 text-genie-text-secondary">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                <p>Loading genie creation logs...</p>
              </div>
            ) : logsData?.data.length === 0 ? (
              <div className="text-center py-8 text-genie-text-muted">
                No genie creation logs found
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {logsData?.data.map((log) => (
                  <div key={log.id} className="border border-genie-border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="font-medium text-genie-text-primary text-sm">
                          {log.agent_name || "Unnamed"}
                        </p>
                        <p className="text-xs text-genie-text-secondary break-words">
                          <CopyableEmail email={log.user_email} />
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewConversations(log)}
                          disabled={!log.setup_conversation_id}
                          title={log.setup_conversation_id ? "View setup conversation for this agent" : "No setup conversation available"}
                        >
                          <Phone className={`h-4 w-4 ${!log.setup_conversation_id ? "opacity-50" : ""}`} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetails(log)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {getStatusBadge(log.status)}

                    <div className="text-xs text-genie-text-secondary space-y-1">
                      <p><span className="text-genie-text-muted">Source:</span> {log.creation_source || "—"}</p>
                      <p><span className="text-genie-text-muted">Retries:</span> {log.retry_count || 0}</p>
                      <p><span className="text-genie-text-muted">Created:</span> {new Date(log.created_at).toLocaleString()}</p>
                      {log.completed_at && (
                        <p><span className="text-genie-text-muted">Completed:</span> {new Date(log.completed_at).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {logsData && logsData.count > 50 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <div className="text-genie-text-secondary order-2 sm:order-1">
                Showing {(page - 1) * 50 + 1} to {Math.min(page * 50, logsData.count)} of{" "}
                {logsData.count} logs
              </div>
              <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="flex-1 sm:flex-none"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page * 50 >= logsData.count}
                  className="flex-1 sm:flex-none"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Drawer */}
        <AgentCreationLogDetailsSidebar
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          selectedLog={selectedLog}
          getStatusBadge={getStatusBadge}
        />
      </div>
    </div>
  );
};

export default AgentCreationLogsPage;
